"use strict";
/**
 * @license
 * Copyright 2024 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use viewer file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * @file Builds upon the Python screenshot tool to allow viewer screenshots to be taken and saved.
 */
import { throttle } from "lodash-es";
import { numChunkStatistics } from "#src/chunk_manager/base.js";
import { SliceViewPanel } from "#src/sliceview/panel.js";
import { StatusMessage } from "#src/status.js";
import {
  columnSpecifications,
  getChunkSourceIdentifier,
  getFormattedNames
} from "#src/ui/statistics.js";
import { RefCounted } from "#src/util/disposable.js";
import { NullarySignal, Signal } from "#src/util/signal.js";
import { ScreenshotMode } from "#src/util/trackable_screenshot_mode.js";
import {
  calculatePanelViewportBounds
} from "#src/util/viewer_resolution_stats.js";
export const MAX_RENDER_AREA_PIXELS = 5100 * 5100;
const SCREENSHOT_TIMEOUT = 3e3;
function saveBlobToFile(blob, filename) {
  const a = document.createElement("a");
  const url = URL.createObjectURL(blob);
  a.href = url;
  a.download = filename;
  try {
    a.click();
  } finally {
    URL.revokeObjectURL(url);
  }
}
function setExtension(filename, extension = ".png") {
  function replaceExtension(filename2) {
    const lastDot = filename2.lastIndexOf(".");
    if (lastDot === -1) {
      return filename2 + extension;
    }
    return `${filename2.substring(0, lastDot)}${extension}`;
  }
  return filename.endsWith(extension) ? filename : replaceExtension(filename);
}
function canvasToBlob(canvas, type) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error("Canvas toBlob failed"));
      }
    }, type);
  });
}
async function extractViewportScreenshot(viewer, viewportBounds) {
  const cropWidth = viewportBounds.right - viewportBounds.left;
  const cropHeight = viewportBounds.bottom - viewportBounds.top;
  const img = await createImageBitmap(
    viewer.display.canvas,
    viewportBounds.left,
    viewportBounds.top,
    cropWidth,
    cropHeight
  );
  const screenshotCanvas = document.createElement("canvas");
  screenshotCanvas.width = cropWidth;
  screenshotCanvas.height = cropHeight;
  const ctx = screenshotCanvas.getContext("2d");
  if (!ctx) throw new Error("Failed to get canvas context");
  ctx.drawImage(img, 0, 0);
  const croppedBlob = await canvasToBlob(screenshotCanvas, "image/png");
  return croppedBlob;
}
export class ScreenshotManager extends RefCounted {
  constructor(viewer) {
    super();
    this.viewer = viewer;
    this.viewer = viewer;
    this.registerDisposer(
      this.viewer.screenshotHandler.sendScreenshotRequested.add(
        (actionState) => {
          this.saveScreenshot(actionState);
        }
      )
    );
    this.registerDisposer(
      this.viewer.screenshotHandler.sendStatisticsRequested.add(
        (actionState) => {
          const newLoadStats = {
            ...actionState.screenshotStatistics.total,
            timestamp: Date.now(),
            gpuMemoryCapacity: this.viewer.chunkQueueManager.capacities.gpuMemory.sizeLimit.value
          };
          this.checkAndHandleStalledScreenshot(actionState, newLoadStats);
          this.screenshotLoadStats = newLoadStats;
        }
      )
    );
    this.registerDisposer(
      this.viewer.display.updateFinished.add(() => {
        this.lastUpdateTimestamp = Date.now();
        this.throttledSendStatistics();
      })
    );
    this.registerDisposer(
      this.viewer.display.screenshotMode.changed.add(() => {
        this.handleScreenshotModeChange();
      })
    );
  }
  screenshotId = -1;
  screenshotLoadStats = null;
  screenshotStartTime = 0;
  screenshotMode = ScreenshotMode.OFF;
  statisticsUpdated = new Signal();
  screenshotFinished = new NullarySignal();
  zoomMaybeChanged = new NullarySignal();
  _shouldKeepSliceViewFOVFixed = true;
  _screenshotScale = 1;
  filename = "";
  lastUpdateTimestamp = 0;
  gpuMemoryChangeTimestamp = 0;
  throttledSendStatistics = this.registerCancellable(
    throttle(async () => {
      const map = await this.viewer.chunkQueueManager.getStatistics();
      if (this.wasDisposed) return;
      const formattedNames = getFormattedNames(
        Array.from(map, (x) => getChunkSourceIdentifier(x[0]))
      );
      let i = 0;
      const rows = [];
      const sumStatistics = new Float64Array(numChunkStatistics);
      for (const [source, statistics] of map) {
        for (let i2 = 0; i2 < numChunkStatistics; ++i2) {
          sumStatistics[i2] += statistics[i2];
        }
        const row = {};
        row.id = getChunkSourceIdentifier(source);
        row.distinctId = formattedNames[i];
        for (const column of columnSpecifications) {
          row[column.key] = column.getter(statistics);
        }
        ++i;
        rows.push(row);
      }
      const total = {};
      for (const column of columnSpecifications) {
        total[column.key] = column.getter(sumStatistics);
      }
      const screenshotLoadStats = {
        ...total,
        timestamp: Date.now(),
        gpuMemoryCapacity: this.viewer.chunkQueueManager.capacities.gpuMemory.sizeLimit.value
      };
      this.statisticsUpdated.dispatch(screenshotLoadStats);
    }, 1e3)
  );
  get screenshotScale() {
    return this._screenshotScale;
  }
  set screenshotScale(scale) {
    this._screenshotScale = this.handleScreenshotZoomAndResize(scale);
    this.zoomMaybeChanged.dispatch();
  }
  get shouldKeepSliceViewFOVFixed() {
    return this._shouldKeepSliceViewFOVFixed;
  }
  set shouldKeepSliceViewFOVFixed(enableFixedFOV) {
    const wasInFixedFOVMode = this.shouldKeepSliceViewFOVFixed;
    this._shouldKeepSliceViewFOVFixed = enableFixedFOV;
    if (!enableFixedFOV && wasInFixedFOVMode) {
      this.handleScreenshotZoomAndResize(
        this.screenshotScale,
        true
      );
      this.zoomMaybeChanged.dispatch();
    } else if (enableFixedFOV && !wasInFixedFOVMode) {
      this.handleScreenshotZoomAndResize(
        1 / this.screenshotScale,
        true
      );
      this.zoomMaybeChanged.dispatch();
    }
  }
  previewScreenshot() {
    this.viewer.display.screenshotMode.value = ScreenshotMode.PREVIEW;
  }
  takeScreenshot(filename = "") {
    this.filename = filename;
    this.viewer.display.screenshotMode.value = ScreenshotMode.ON;
  }
  forceScreenshot() {
    this.viewer.display.screenshotMode.value = ScreenshotMode.FORCE;
  }
  cancelScreenshot(shouldStayInPreview = false) {
    if (this.screenshotMode === ScreenshotMode.ON) {
      this.screenshotId--;
    }
    const newMode = shouldStayInPreview ? ScreenshotMode.PREVIEW : ScreenshotMode.OFF;
    this.viewer.display.screenshotMode.value = newMode;
  }
  // Calculates the cropped area of the viewport panels
  calculatedClippedViewportSize() {
    const renderingPanelArea = calculatePanelViewportBounds(
      this.viewer.display.panels
    ).totalRenderPanelViewport;
    return {
      width: Math.round(renderingPanelArea.right - renderingPanelArea.left),
      height: Math.round(renderingPanelArea.bottom - renderingPanelArea.top)
    };
  }
  handleScreenshotStarted() {
    this.screenshotStartTime = this.lastUpdateTimestamp = this.gpuMemoryChangeTimestamp = Date.now();
    this.screenshotLoadStats = null;
    this.screenshotId++;
    this.viewer.screenshotHandler.requestState.value = this.screenshotId.toString();
  }
  resizeCanvasIfNeeded(scale = this.screenshotScale) {
    const shouldChangeCanvasSize = scale !== 1;
    const { viewer } = this;
    if (shouldChangeCanvasSize) {
      const oldSize = {
        width: viewer.display.canvas.width,
        height: viewer.display.canvas.height
      };
      const newSize = {
        width: Math.round(oldSize.width * scale),
        height: Math.round(oldSize.height * scale)
      };
      viewer.display.canvas.width = newSize.width;
      viewer.display.canvas.height = newSize.height;
      viewer.display.resizeCallback();
    }
  }
  handleScreenshotModeChange() {
    const { display } = this.viewer;
    const mayNeedCanvasResize = this.screenshotMode === ScreenshotMode.OFF;
    this.screenshotMode = display.screenshotMode.value;
    switch (this.screenshotMode) {
      case ScreenshotMode.OFF:
        this.resetCanvasSize();
        this.resetStatistics();
        this.viewer.screenshotHandler.requestState.value = void 0;
        break;
      case ScreenshotMode.FORCE:
        display.scheduleRedraw();
        break;
      case ScreenshotMode.ON:
        if (mayNeedCanvasResize) {
          this.resizeCanvasIfNeeded();
        }
        this.handleScreenshotStarted();
        break;
      case ScreenshotMode.PREVIEW:
        break;
    }
  }
  /**
   * Handles the zooming of the screenshot in fixed FOV mode.
   * This supports:
   * 1. Updating the zoom level of the viewer to match the screenshot scale.
   * 2. Resetting the zoom level of the slice views to the original level.
   * 3. Resizing the canvas to match the new scale.
   * @param scale - The scale factor to apply to the screenshot.
   * @param resetZoom - If true, the zoom resets to the original level.
   */
  handleScreenshotZoomAndResize(scale, resetZoom = false) {
    const oldScale = this.screenshotScale;
    let intendedScale = scale;
    if (!resetZoom && scale > 1) {
      const currentCanvasSize = this.calculatedClippedViewportSize();
      const numPixels = currentCanvasSize.width * currentCanvasSize.height / (oldScale * oldScale);
      if (numPixels * intendedScale * intendedScale > MAX_RENDER_AREA_PIXELS) {
        intendedScale = Math.sqrt(MAX_RENDER_AREA_PIXELS / numPixels);
      }
    }
    const scaleFactor = intendedScale / oldScale;
    const zoomScaleFactor = resetZoom ? scale : 1 / scaleFactor;
    const canvasScaleFactor = resetZoom ? 1 : scaleFactor;
    if (this.shouldKeepSliceViewFOVFixed || resetZoom) {
      const { navigationState } = this.viewer;
      for (const panel of this.viewer.display.panels) {
        if (panel instanceof SliceViewPanel) {
          const zoom = navigationState.zoomFactor.value;
          navigationState.zoomFactor.value = zoom * zoomScaleFactor;
          break;
        }
      }
    }
    this.resizeCanvasIfNeeded(canvasScaleFactor);
    return intendedScale;
  }
  /**
   * Check if the screenshot is stuck by comparing the number of visible chunks
   * in the GPU with the previous number of visible chunks. If the number of
   * visible chunks has not changed after a certain timeout, and the display has not updated, force a screenshot.
   */
  checkAndHandleStalledScreenshot(actionState, fullStats) {
    if (this.screenshotLoadStats === null) {
      return;
    }
    const total = actionState.screenshotStatistics.total;
    const newStats = {
      visibleChunksGpuMemory: total.visibleChunksGpuMemory,
      timestamp: Date.now(),
      totalGpuMemory: this.viewer.chunkQueueManager.capacities.gpuMemory.sizeLimit.value,
      numDownloadingChunks: total.visibleChunksDownloading
    };
    const oldStats = this.screenshotLoadStats;
    if (oldStats.visibleChunksGpuMemory === newStats.visibleChunksGpuMemory && (oldStats.gpuMemoryCapacity === newStats.totalGpuMemory || newStats.numDownloadingChunks == 0)) {
      if (newStats.timestamp - this.gpuMemoryChangeTimestamp > SCREENSHOT_TIMEOUT && Date.now() - this.lastUpdateTimestamp > SCREENSHOT_TIMEOUT) {
        this.statisticsUpdated.dispatch(fullStats);
        const message = `Forcing screenshot: screenshot is likely stuck, no change in GPU chunks after ${SCREENSHOT_TIMEOUT}ms. Last visible chunks: ${total.visibleChunksGpuMemory}/${total.visibleChunksTotal}`;
        console.warn(message);
        StatusMessage.showTemporaryMessage(message, 5e3);
        this.forceScreenshot();
      }
    } else {
      this.gpuMemoryChangeTimestamp = newStats.timestamp;
    }
  }
  async saveScreenshot(actionState) {
    const { screenshot } = actionState;
    const { imageType } = screenshot;
    if (imageType !== "image/png") {
      console.error("Image type is not PNG");
      this.viewer.display.screenshotMode.value = ScreenshotMode.OFF;
      return;
    }
    const renderingPanelArea = calculatePanelViewportBounds(
      this.viewer.display.panels
    ).totalRenderPanelViewport;
    try {
      const croppedImage = await extractViewportScreenshot(
        this.viewer,
        renderingPanelArea
      );
      this.generateFilename(
        renderingPanelArea.right - renderingPanelArea.left,
        renderingPanelArea.bottom - renderingPanelArea.top
      );
      saveBlobToFile(croppedImage, this.filename);
    } catch (error) {
      console.error("Failed to save screenshot:", error);
    } finally {
      this.viewer.display.screenshotMode.value = ScreenshotMode.OFF;
      this.screenshotFinished.dispatch();
    }
  }
  resetCanvasSize() {
    const { viewer } = this;
    ++viewer.display.resizeGeneration;
    viewer.display.resizeCallback();
  }
  resetStatistics() {
    this.screenshotLoadStats = null;
  }
  generateFilename(width, height) {
    if (!this.filename) {
      const nowtime = (/* @__PURE__ */ new Date()).toLocaleString().replace(", ", "-");
      this.filename = `neuroglancer-screenshot-w${width}px-h${height}px-at-${nowtime}`;
    }
    this.filename = setExtension(this.filename);
    return this.filename;
  }
}
